"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ArrowRight, Loader } from "lucide-react";
function ClubForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center gap-6 py-8">
        <svg className="size-16 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
        animationDuration: "3s"
      }}>
          <circle cx="50" cy="50" r="48" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
          <circle cx="50" cy="50" r="38" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
          <circle cx="50" cy="50" r="28" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1" />
          <circle cx="50" cy="50" r="18" fill="hsl(210 100% 50%)" />
          <circle cx="50" cy="50" r="4" fill="hsl(240 10% 4%)" />
        </svg>
        <p className="font-default italic text-2xl md:text-3xl text-white text-center">
          Welcome to the inner circle. Check your inbox.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-8">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-3 items-end">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 w-full">
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ENTER.YOUR@EMAIL.COM" className="h-12 bg-transparent border-0 border-b border-white/40 px-0 py-2 font-mono uppercase text-sm tracking-wider text-white placeholder:text-white/40 focus-visible:border-primary focus-visible:ring-0 focus-visible:border-b-2 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs uppercase tracking-wider text-destructive" />
            </FormItem>} />

        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="hidden">
              <FormControl>
                <Input type="hidden" {...field} />
              </FormControl>
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className="h-12 px-6 bg-primary text-white hover:bg-primary/90 font-mono uppercase text-xs tracking-widest font-semibold shadow-md w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              PROCESSING...
            </> : <>
              JOIN NOW <ArrowRight className="size-4" />
            </>}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-white/60">
        <span>Tech Updates</span>
        <span className="text-primary">·</span>
        <span>Exclusive Deals</span>
        <span className="text-primary">·</span>
        <span>Zero Spam</span>
      </div>

      <FormField control={control} name="consent" rules={{
      required: "Please confirm to receive updates"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 justify-center">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-white/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white" />
            </FormControl>
            <div className="space-y-1">
              <FormLabel className="text-sm text-white/80 font-normal cursor-pointer">
                Send me repair tips and trade-in alerts
              </FormLabel>
              <FormMessage className="font-mono text-xs uppercase tracking-wider text-destructive" />
            </div>
          </FormItem>} />

      <FormField control={control} name="favorite_genre" render={({
      field
    }) => <FormItem className="hidden">
            <FormControl>
              <Input type="hidden" {...field} />
            </FormControl>
          </FormItem>} />

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem className="hidden">
            <FormControl>
              <Input type="hidden" {...field} />
            </FormControl>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm font-mono uppercase tracking-wider text-center">{submitError}</p>
        </div>}
    </Form>;
}
export default function JoinTheTechwireCommunity() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="464" id="join-the-techwire-community" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4">
      <div className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-luminosity" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701038/img/techwire_hero_circuit.png)"
    }} aria-hidden="true" />

      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 1px 1px, hsl(0 0% 100%) 1px, transparent 0)",
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <svg className="w-[800px] h-[800px] md:w-[1000px] md:h-[1000px] opacity-[0.05]" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
        animation: "spin 60s linear infinite"
      }}>
          {Array.from({
          length: 24
        }).map((_, i) => <circle key={i} data-index={i} cx="500" cy="500" r={60 + i * 18} stroke="currentColor" strokeWidth="0.8" fill="none" />)}
          <circle cx="500" cy="500" r="80" fill="hsl(210 100% 50%)" opacity="0.6" />
          <circle cx="500" cy="500" r="10" fill="hsl(240 10% 4%)" />
        </svg>
      </div>

      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 0%, hsl(240 10% 4%) 85%)"
    }} aria-hidden="true" />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div ref={ref} className={`relative z-10 max-w-[720px] mx-auto flex flex-col items-center text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="inline-block mb-10 px-5 py-2 bg-primary text-white font-mono text-xs uppercase tracking-[0.2em] font-bold shadow-lg">
           VIP TECH ALERTS 
        </div>

        <h2 className="font-default text-white leading-[0.95] mb-8 tracking-tight">
          <span className="block text-6xl md:text-8xl lg:text-9xl font-extrabold">
            Join the
          </span>
          <span className="block text-6xl md:text-8xl lg:text-9xl font-extrabold italic text-primary">
            TechWire.
          </span>
        </h2>

        <p className="text-base md:text-[17px] leading-relaxed text-white/75 max-w-[540px] mb-12 font-default">
          Get priority access to flagship smartphone drops, exclusive trade-in bonuses, and expert maintenance guides. Stay ahead of the curve.
        </p>

        <div className="w-full">
          <FormProvider formKey="join_community_form_home" formId="join_community_form_home" sectionName="join-the-techwire-community" defaultValues={{
          name: "Tech Enthusiast",
          email: "",
          favorite_genre: "Other",
          address: "",
          consent: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="JOIN NOW" successMessage="Welcome to the inner circle. Check your inbox." errorMessage="Something went wrong. Please try again.">
            <ClubForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}